import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Flex, Text, TextInput, Tooltip } from "@mantine/core";
import { type CellContext, createColumnHelper } from "@tanstack/react-table";
import { type FocusEvent, useState } from "react";

import { ConfirmationPopover } from "@/components/ui/confirmation-popover";
import { UpwageBadge } from "@/components/upwage-badge";
import { INFO } from "@/components/upwage-badge/upwage-badge";
import type {
  CompanyManagementTableMeta,
  CompanyManagementTableRow,
} from "@/types/company-manage";
import { ScreenerLocationStatus } from "@/types/screener-location";
import { isEmptyAfterTrim } from "@/utils/string";

const getMeta = (
  info: CellContext<CompanyManagementTableRow, unknown>,
): CompanyManagementTableMeta => {
  const { meta } = info.table.options;

  if (!meta?.companyManagement) {
    throw new TypeError(
      "Attempted to resolve Company Management Table meta but it was not defined.",
    );
  }

  return meta.companyManagement;
};

const DUPLICATE_NAME_MESSAGE = "A company with this name already exists";

const nameIsDuplicate = (
  name: string,
  info: CellContext<CompanyManagementTableRow, unknown>,
): boolean => {
  const trimmedLower = name.trim().toLowerCase();
  if (!trimmedLower) {
    return false;
  }

  return info.table
    .getRowModel()
    .rows.some(
      (row) =>
        row.id !== info.row.id &&
        !row.original.isDraft &&
        row.original.name.toLowerCase() === trimmedLower,
    );
};

const columnHelper = createColumnHelper<CompanyManagementTableRow>();

const INPUT_CLASS_NAMES = {
  input: "border-uw-gray-2 h-10 rounded text-sm",
};

const trimOnBlur =
  (onUpdate: (trimmed: string) => void) =>
  (e: FocusEvent<HTMLInputElement>) => {
    const trimmed = e.currentTarget.value.trim();
    if (trimmed !== e.currentTarget.value) {
      onUpdate(trimmed);
    }
  };

const CompanyNameInput = ({
  error,
  onChange,
  onTrimmedBlur,
  placeholder,
  value,
}: {
  error?: string;
  onChange: (value: string) => void;
  onTrimmedBlur: (trimmed: string) => void;
  placeholder?: string;
  value: string;
}) => (
  <TextInput
    classNames={INPUT_CLASS_NAMES}
    error={error ?? null}
    onBlur={trimOnBlur(onTrimmedBlur)}
    onChange={(e) => onChange(e.currentTarget.value)}
    placeholder={placeholder}
    value={value}
  />
);

const NameCell = (info: CellContext<CompanyManagementTableRow, string>) => {
  const row = info.row.original;
  const meta = getMeta(info);
  const isDuplicate = row.isDraft && nameIsDuplicate(row.name, info);
  const duplicateError = isDuplicate ? DUPLICATE_NAME_MESSAGE : undefined;

  if (row.isDraft) {
    return (
      <CompanyNameInput
        error={duplicateError}
        onChange={meta.onUpdateDraft}
        onTrimmedBlur={meta.onUpdateDraft}
        placeholder="Name"
        value={row.name}
      />
    );
  }

  return (
    <Text fw={600} size="sm" c="gray.9">
      {row.name}
    </Text>
  );
};

const StatusCell = (info: CellContext<CompanyManagementTableRow, boolean>) => {
  const row = info.row.original;

  if (row.isDraft) {
    return null;
  }

  return (
    <UpwageBadge
      colorVariant={row.isDeleted ? INFO : ScreenerLocationStatus.Live}
      className="w-full"
      filled
    >
      {row.isDeleted ? "Inactive" : "Active"}
    </UpwageBadge>
  );
};

const ACTION_BUTTON_CLASS =
  "gap-1.5 uppercase text-xs font-semibold tracking-wider";
const DISABLED_TRANSPARENT_CLASS = `${ACTION_BUTTON_CLASS} disabled:bg-transparent disabled:opacity-50`;

const DeactivateButton = ({
  companyId,
  meta,
}: {
  companyId: string;
  meta: CompanyManagementTableMeta;
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const isActiveCompany = companyId === meta.activeCompanyId;

  return (
    <ConfirmationPopover
      isDanger
      isOpen={isConfirmOpen}
      message="Are you sure you want to deactivate this company? Users will immediately lose access to the Talent Hub."
      onClose={() => setIsConfirmOpen(false)}
      onPrimaryButtonClick={() => meta.onDeactivate(companyId)}
      primaryButtonContent="Deactivate"
    >
      <Button
        className={DISABLED_TRANSPARENT_CLASS}
        c="red.3"
        disabled={isActiveCompany || meta.isOperationInProgress}
        onClick={() => setIsConfirmOpen(true)}
        p={0}
        rightSection={<FontAwesomeIcon icon={faXmark} size="lg" />}
        size="compact-sm"
        variant="transparent"
      >
        Deactivate
      </Button>
    </ConfirmationPopover>
  );
};

const ReactivateButton = ({
  companyId,
  meta,
}: {
  companyId: string;
  meta: CompanyManagementTableMeta;
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  return (
    <ConfirmationPopover
      isOpen={isConfirmOpen}
      message="Are you sure you want to reactivate this company? Users will immediately regain access to the Talent Hub."
      onClose={() => setIsConfirmOpen(false)}
      onPrimaryButtonClick={() => meta.onReactivate(companyId)}
      primaryButtonContent="Reactivate"
    >
      <Button
        className={DISABLED_TRANSPARENT_CLASS}
        c="purple.5"
        disabled={meta.isOperationInProgress}
        onClick={() => setIsConfirmOpen(true)}
        p={0}
        size="compact-sm"
        rightSection={<FontAwesomeIcon icon={faPlus} size="lg" />}
        variant="transparent"
      >
        Reactivate
      </Button>
    </ConfirmationPopover>
  );
};

const CancelButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    className="uppercase text-xs font-semibold tracking-wider"
    c="red.3"
    onClick={onClick}
    p={0}
    size="compact-sm"
    variant="transparent"
  >
    Cancel
  </Button>
);

const AddButton = ({
  isDuplicate,
  meta,
  name,
}: {
  isDuplicate: boolean;
  meta: CompanyManagementTableMeta;
  name: string;
}) => {
  const trimmedName = name.trim();
  const isEmpty = isEmptyAfterTrim(trimmedName);
  const isDisabled = isEmpty || isDuplicate;

  const disabledReason = isDuplicate
    ? DUPLICATE_NAME_MESSAGE
    : isEmpty
      ? "Company name is required"
      : undefined;

  const button = (
    <Button
      className="disabled:opacity-50 h-8 px-3 gap-1 text-sm font-semibold uppercase"
      color="purple.5"
      disabled={isDisabled}
      loading={meta.isAddPending}
      onClick={() => meta.onAdd(trimmedName)}
      radius={4}
      rightSection={<FontAwesomeIcon icon={faPlus} size="sm" />}
    >
      Add
    </Button>
  );

  if (isDisabled && disabledReason) {
    return (
      <Tooltip label={disabledReason} withArrow>
        {/* Span wrapper required — disabled buttons don't fire hover events
            for the Tooltip to detect */}
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};

const ActionsCell = (info: CellContext<CompanyManagementTableRow, unknown>) => {
  const row = info.row.original;
  const meta = getMeta(info);

  if (row.isDraft) {
    const isDuplicate = nameIsDuplicate(row.name, info);
    return (
      <Flex align="center" gap={12} justify="space-between">
        <AddButton isDuplicate={isDuplicate} meta={meta} name={row.name} />
        <CancelButton onClick={meta.onCancelAdd} />
      </Flex>
    );
  }

  return (
    <Flex align="center" gap={12} justify="space-between">
      {row.isDeleted ? (
        <ReactivateButton companyId={row.id} meta={meta} />
      ) : (
        <DeactivateButton companyId={row.id} meta={meta} />
      )}
      <Button
        className={DISABLED_TRANSPARENT_CLASS}
        c="purple.5"
        disabled={meta.isOperationInProgress}
        onClick={() => meta.onEdit(row.id)}
        p={0}
        rightSection={
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
        }
        size="compact-sm"
        variant="transparent"
      >
        Edit
      </Button>
    </Flex>
  );
};

const headerProps = {
  c: "gray.9",
  className: "tracking-wider uppercase",
  fw: 600,
  size: "xs",
};

export const columns = [
  columnHelper.accessor("name", {
    cell: NameCell,
    header: () => <Text {...headerProps}>Name</Text>,
    size: 300,
  }),
  columnHelper.display({
    id: "users",
    cell: (info) => {
      const row = info.row.original;
      return (
        <Text size="sm" c={row.isDraft ? "gray.5" : "gray.9"}>
          {row.isDraft ? 0 : row.userCount}
        </Text>
      );
    },
    header: () => <Text {...headerProps}>Users</Text>,
    size: 80,
  }),
  columnHelper.accessor((row) => (row.isDraft ? false : row.isDeleted), {
    id: "status",
    cell: StatusCell,
    header: () => <Text {...headerProps}>Status</Text>,
    size: 140,
  }),
  columnHelper.display({
    id: "actions",
    cell: ActionsCell,
    size: 220,
  }),
];
